/**
 * Level 5 — the Terror Hotel.
 *
 * "An infinite hotel complex with many rooms and halls", built in the 1930s and
 * furnished in the 1920s: mahogany-red and gold wallpaper, dark walnut and red
 * and gold carpet, and — the detail the whole level turns on — *it is clean*.
 * "The level is mostly clean, with little dust and dirt lingering on surfaces."
 * Somewhere in the walls, something is still doing the housekeeping.
 *
 * The shape is double-loaded corridors, which no other level in this game has.
 * A long straight run with a door every five metres on both sides, all the way
 * out to the fog, is a completely different kind of lost from a partitioned
 * plate or a maze: you are not disoriented, you can see exactly where you are
 * going, and it does not end. The guest rooms back onto each other across a
 * party wall, so the building is honestly built — every room is a real room
 * with one door onto the corridor it belongs to.
 *
 * Two things break the run: a handful of cross corridors, and the Beverly Room,
 * the ballroom the wiki puts at the middle of the level.
 */

import { Cell } from '../../core/types';
import { scatterDamp } from './decorate';
import {
  addRegion,
  fillRect,
  inset,
  interiorRect,
  pillarLattice,
  type GenSite,
  type Rect,
} from './site';
import { applyWrongness } from './wrongness';

/** Corridor width in cells. A hotel corridor is generous; two abreast is it. */
const CORRIDOR = 2;
/** Cells from one corridor's near edge to the next. Sets the guest room depth. */
const PITCH: readonly [number, number] = [13, 16];
/** Guest room frontage onto the corridor, in cells. */
const FRONTAGE: readonly [number, number] = [5, 8];
/** Shallowest guest room that is still a room and not a wardrobe. */
const MIN_DEPTH = 3;

/** A span of x reserved by something that is not a guest room. */
interface Reserved {
  x0: number;
  x1: number;
}

/** Carve the Terror Hotel profile into `site`. */
export function generateHotel(site: GenSite): void {
  const { grid, rng } = site;
  const bounds = interiorRect(grid);

  // The corridors run east-west. Real hotels have a primary direction and this
  // is what gives the level its one hundred metre view.
  const lanes: number[] = [];
  // Far enough in that the strip outboard of the first corridor is deep enough
  // to hold a row of back-of-house rooms. See `carveBackOfHouse`.
  let y = bounds.y + rng.int(7, 11);
  while (y + CORRIDOR <= bounds.y + bounds.h - 2) {
    lanes.push(y);
    fillRect(grid, { x: bounds.x, y, w: bounds.w, h: CORRIDOR }, Cell.Floor);
    addRegion(site, { x: bounds.x, y, w: bounds.w, h: CORRIDOR }, 'hall');
    y += rng.int(PITCH[0], PITCH[1]);
  }
  if (lanes.length < 2) return;

  // Cross corridors, so the level is not a set of parallel worlds. Carved before
  // the rooms and reserved against them: a corridor cutting a guest room in half
  // is the one kind of wrong this level should not be.
  const crossings: Reserved[] = [];
  const crossCount = rng.int(2, 3);
  for (let c = 0; c < crossCount; c++) {
    const band = bounds.w / (crossCount + 1);
    const x = Math.round(bounds.x + band * (c + 1) + rng.range(-band * 0.2, band * 0.2));
    const x0 = Math.max(bounds.x, x);
    const x1 = Math.min(bounds.x + bounds.w - 1, x0 + CORRIDOR - 1);
    fillRect(grid, { x: x0, y: bounds.y, w: x1 - x0 + 1, h: bounds.h }, Cell.Floor);
    addRegion(site, { x: x0, y: bounds.y, w: x1 - x0 + 1, h: bounds.h }, 'hall');
    crossings.push({ x0, x1 });
  }

  // The Beverly Room. One band, one span, cleared to the full depth of the
  // block: the only place on the level you can stand more than four metres from
  // a wall, and the only one with a ceiling worth looking at.
  const ballroomBand = lanes.length >= 3 ? rng.int(1, lanes.length - 2) : 0;
  const ballroom = planBallroom(site, bounds, crossings);

  for (let b = 0; b + 1 < lanes.length; b++) {
    const a = lanes[b]! + CORRIDOR;
    const bEnd = lanes[b + 1]! - 1;
    const reserved = b === ballroomBand && ballroom ? [...crossings, ballroom] : crossings;
    carveBlock(site, bounds, a, bEnd, reserved);
    if (b === ballroomBand && ballroom) carveBallroom(site, a, bEnd, ballroom);
  }

  // The band outside the first and last corridor is service space: no doors on
  // the outboard side, which is what stops the level looking like a plan view.
  carveBackOfHouse(site, bounds, lanes, crossings);

  // Sparing, and weighted at the two operations that read in a corridor: a door
  // in the run that has never opened, and a room built inside a guest room. The
  // level is clean and well kept, so nothing here should look damaged — only
  // wrong.
  applyWrongness(site, {
    innerRooms: [5, 9],
    doorsToNothing: [6, 11],
    perforatedWalls: [0, 1],
    pillarsInDoorways: [1, 2],
    wallFins: [2, 5],
    tightPillarPatches: [0, 1],
  });

  // Housekeeping does not reach the boiler room, and the pipes down there leak
  // Almond Water. A dozen patches is the only dirt on the level.
  scatterDamp(site, rng.int(6, 12), 4, 14);
}

/** Pick the ballroom's x span, clear of every cross corridor. */
function planBallroom(site: GenSite, bounds: Rect, crossings: readonly Reserved[]): Reserved | null {
  const { rng } = site;
  for (let tries = 0; tries < 30; tries++) {
    const w = rng.int(16, 24);
    const x0 = rng.int(bounds.x + 2, bounds.x + bounds.w - w - 2);
    const x1 = x0 + w - 1;
    if (crossings.some((c) => c.x0 <= x1 + 2 && x0 - 2 <= c.x1)) continue;
    return { x0, x1 };
  }
  return null;
}

/** The ballroom itself: one open span, a colonnade, and no doors. */
function carveBallroom(site: GenSite, a: number, bEnd: number, span: Reserved): void {
  const { grid, rng } = site;
  // Inside the block's own walls, so the ballroom still has a front and a back
  // that the openings are punched through rather than being a hole in the plan.
  const r: Rect = { x: span.x0, y: a + 1, w: span.x1 - span.x0 + 1, h: bEnd - a - 1 };
  if (r.w < 6 || r.h < 6) return;
  fillRect(grid, r, Cell.Floor);
  pillarLattice(grid, inset(r, 1), rng.int(4, 5), 2);
  addRegion(site, r, 'atrium');
  // Open to both corridors across most of its frontage. You should be able to
  // see the far wall of it from the corridor and walk in without a decision.
  const openW = Math.max(4, Math.round(r.w * rng.range(0.45, 0.7)));
  const ox = r.x + (((r.w - openW) / 2) | 0);
  for (let k = 0; k < openW; k++) {
    grid.set(ox + k, a, Cell.Doorway);
    grid.set(ox + k, bEnd, Cell.Doorway);
  }
}

/**
 * One block of guest rooms: two rows of them back to back, the front row
 * opening onto the corridor above and the back row onto the corridor below,
 * with the party wall between.
 */
function carveBlock(
  site: GenSite,
  bounds: Rect,
  a: number,
  bEnd: number,
  reserved: readonly Reserved[],
): void {
  const { rng } = site;
  const depth = bEnd - a + 1;
  // a is the wall against the upper corridor, bEnd the wall against the lower
  // one, and `mid` the party wall. Both rooms need MIN_DEPTH inside that.
  if (depth < 2 * MIN_DEPTH + 3) return;
  const mid = a + (depth >> 1);

  let x = bounds.x + 1;
  const limit = bounds.x + bounds.w - 1;
  while (x < limit) {
    const block = reserved.find((c) => c.x0 <= x && x <= c.x1);
    if (block) {
      x = block.x1 + 2;
      continue;
    }
    let width = rng.int(FRONTAGE[0], FRONTAGE[1]);
    // Stop the frontage short of anything reserved rather than running into it.
    for (const c of reserved) {
      if (c.x0 > x && c.x0 - x < width) width = c.x0 - x;
    }
    if (x + width > limit) width = limit - x;
    if (width >= 2) {
      carveRoom(site, { x, y: a + 1, w: width, h: mid - a - 1 }, a, true);
      carveRoom(site, { x, y: mid + 1, w: width, h: bEnd - mid - 1 }, bEnd, false);
    }
    // The wall between this room and the next. A frontage too narrow to build on
    // still advances the cursor, or a room clipped by a cross corridor would
    // abandon the rest of the block.
    x += (width >= 2 ? width : 1) + 1;
  }
}

/**
 * One guest room. `doorLine` is the row of the wall it shares with its corridor;
 * `above` says which side of the room that wall is on.
 */
function carveRoom(site: GenSite, r: Rect, doorLine: number, above: boolean): void {
  const { grid, rng } = site;
  if (r.w < 2 || r.h < MIN_DEPTH) return;
  fillRect(grid, r, Cell.Floor);
  grid.set(r.x + rng.int(0, r.w - 1), doorLine, Cell.Doorway);
  addRegion(site, r, r.w * r.h <= 12 ? 'closet' : 'room');

  // A quarter of them have the bathroom partitioned off inside, which is where
  // the level's supplies end up: a closet is scored highest for a cache.
  if (r.w >= 5 && r.h >= 5 && rng.chance(0.25)) {
    const bw = rng.int(2, 3);
    const bh = rng.int(2, 3);
    const bx = rng.chance(0.5) ? r.x : r.x + r.w - bw;
    const by = above ? r.y + r.h - bh : r.y;
    fillRect(grid, { x: bx - 1, y: by - 1, w: bw + 2, h: bh + 2 }, Cell.Wall);
    fillRect(grid, { x: bx, y: by, w: bw, h: bh }, Cell.Floor);
    grid.set(bx + (bw >> 1), above ? by - 1 : by + bh, Cell.Doorway);
    addRegion(site, { x: bx, y: by, w: bw, h: bh }, 'closet');
  }
}

/**
 * The strips outboard of the first and last corridor.
 *
 * Against each outer wall there is a one-cell service run the full length of the
 * level, and between it and the guest corridor a single row of rooms with no
 * numbers on the doors: linen stores, plant, the stair to the boiler room. The
 * service runs are the fastest way across the floor and the only part of the
 * hotel that was not built to be seen — which is also why they are the two
 * places the level is not clean.
 */
function carveBackOfHouse(
  site: GenSite,
  bounds: Rect,
  lanes: readonly number[],
  crossings: readonly Reserved[],
): void {
  const { grid, rng } = site;
  const first = lanes[0]!;
  const last = lanes[lanes.length - 1]!;
  const topRun = bounds.y;
  const bottomRun = bounds.y + bounds.h - 1;

  for (const run of [topRun, bottomRun]) {
    fillRect(grid, { x: bounds.x, y: run, w: bounds.w, h: 1 }, Cell.Floor);
    addRegion(site, { x: bounds.x, y: run, w: bounds.w, h: 1 }, 'hall');
  }

  // Room rows sit between the service run's wall and the guest corridor.
  const strips: Array<{ a: number; bEnd: number; doorLine: number; above: boolean }> = [];
  if (first - 2 - (topRun + 2) + 1 >= MIN_DEPTH) {
    strips.push({ a: topRun + 2, bEnd: first - 2, doorLine: first - 1, above: false });
  }
  const tailTop = last + CORRIDOR + 1;
  if (bottomRun - 2 - tailTop + 1 >= MIN_DEPTH) {
    strips.push({ a: tailTop, bEnd: bottomRun - 2, doorLine: last + CORRIDOR, above: true });
  }

  for (const strip of strips) {
    let x = bounds.x + 1;
    const limit = bounds.x + bounds.w - 1;
    while (x < limit) {
      const block = crossings.find((c) => c.x0 <= x && x <= c.x1);
      if (block) {
        x = block.x1 + 2;
        continue;
      }
      let width = rng.int(4, 9);
      for (const c of crossings) if (c.x0 > x && c.x0 - x < width) width = c.x0 - x;
      if (x + width > limit) width = limit - x;
      // Two thirds of the strip is rooms; the rest stays solid, so the corridor
      // has long blank stretches on its outboard side.
      if (width >= 2 && rng.chance(0.66)) {
        carveRoom(
          site,
          { x, y: strip.a, w: width, h: strip.bEnd - strip.a + 1 },
          strip.doorLine,
          strip.above,
        );
      }
      x += (width >= 2 ? width : 1) + 1;
    }
  }
}
